import { useState } from "react";
import type {
  CreateFireScenarioInput,
  FireScenarioWithProjection,
} from "@money/shared";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { FireProjection, GlossaryResponse } from "@money/shared";
import {
  useFire,
  useCreateFireScenario,
  useDeleteFireScenario,
  useExplainFire,
  useWhatIf,
  useGlossary,
} from "../api/fire";
import { useProfile } from "../api/profile";
import { Loading, ErrorState } from "../components/States";
import { formatCents } from "../lib/format";

const VARIANTS = ["lean", "regular", "fat", "barista", "coast"] as const;

// UI-only presets prefilled when picking a variant; everything stays editable.
const PRESETS: Record<string, Partial<CreateFireScenarioInput>> = {
  lean: { annual_spend_cents: 3_000_000, withdrawal_rate: 0.04, real_return_rate: 0.05 },
  regular: { annual_spend_cents: 5_000_000, withdrawal_rate: 0.04, real_return_rate: 0.06 },
  fat: { annual_spend_cents: 10_000_000, withdrawal_rate: 0.035, real_return_rate: 0.06 },
  barista: { annual_spend_cents: 5_000_000, part_time_income_cents: 2_000_000, withdrawal_rate: 0.04, real_return_rate: 0.06 },
  coast: { annual_spend_cents: 5_000_000, withdrawal_rate: 0.04, real_return_rate: 0.06, target_retirement_age: 65 },
};

export function Fire() {
  const fire = useFire();
  const profile = useProfile();
  const del = useDeleteFireScenario();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">FIRE</h1>
        <div className="text-sm text-slate-400">
          Invested: {fire.data ? formatCents(fire.data.invested_balance_cents) : "—"}
        </div>
      </div>

      {profile.data && profile.data.birth_year == null && (
        <div className="rounded-lg border border-slate-700 bg-slate-900/60 p-3 text-sm text-slate-300">
          Set your birth year in <span className="font-medium">Settings</span> to see a
          projected age of independence (otherwise projections show years from now).
        </div>
      )}

      <AddScenarioForm />

      {fire.isPending && <Loading />}
      {fire.isError && <ErrorState onRetry={() => fire.refetch()} />}
      {fire.data?.scenarios.length === 0 && (
        <div className="text-slate-400 text-sm">No scenarios yet — add one above.</div>
      )}

      <div className="space-y-4">
        {fire.data?.scenarios.map((s) => (
          <ScenarioCard key={s.scenario.id} data={s} onDelete={() => del.mutate(s.scenario.id)} />
        ))}
      </div>

      <WhatIfSandbox />
      <GlossaryLookup />
    </div>
  );
}

function WhatIfSandbox() {
  const whatif = useWhatIf();
  const [spend, setSpend] = useState("50000");
  const [ret, setRet] = useState("6");
  const [withdrawal, setWithdrawal] = useState("4");
  const [contribution, setContribution] = useState("20000");
  const [result, setResult] = useState<FireProjection | null>(null);

  const run = async () => {
    const r = await whatif.mutateAsync({
      annual_spend_cents: Math.round(parseFloat(spend || "0") * 100),
      real_return_rate: parseFloat(ret || "6") / 100,
      withdrawal_rate: parseFloat(withdrawal || "4") / 100,
      annual_contribution_cents: Math.round(parseFloat(contribution || "0") * 100),
    });
    setResult(r);
  };

  const indep =
    result?.projected_age_of_independence != null
      ? `age ${result.projected_age_of_independence}`
      : result?.projected_year_of_independence != null
        ? `${result.projected_year_of_independence}`
        : result
          ? "not within horizon"
          : "—";

  return (
    <section>
      <h2 className="text-sm font-medium text-slate-300 mb-2">What-if sandbox</h2>
      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <p className="text-xs text-slate-500">
          Try assumptions without saving a scenario. Uses your current invested balance.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Field label="Spend ($)">
            <input inputMode="decimal" value={spend} onChange={(e) => setSpend(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Real return (%)">
            <input inputMode="decimal" value={ret} onChange={(e) => setRet(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Withdrawal (%)">
            <input inputMode="decimal" value={withdrawal} onChange={(e) => setWithdrawal(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Contribution ($)">
            <input inputMode="decimal" value={contribution} onChange={(e) => setContribution(e.target.value)} className={inputCls} />
          </Field>
        </div>
        <button onClick={run} disabled={whatif.isPending} className={btnCls}>
          {whatif.isPending ? "Projecting…" : "Project"}
        </button>
        {result && (
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <Stat label="FIRE number" value={formatCents(result.fire_number)} />
            <Stat label="Independence" value={indep} />
          </dl>
        )}
      </div>
    </section>
  );
}

function GlossaryLookup() {
  const glossary = useGlossary();
  const [term, setTerm] = useState("");
  const [result, setResult] = useState<GlossaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onLook = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!term.trim()) return;
    setError(null);
    try {
      setResult(await glossary.mutateAsync(term.trim()));
    } catch {
      setError("Couldn't define that right now.");
    }
  };

  return (
    <section>
      <h2 className="text-sm font-medium text-slate-300 mb-2">Glossary</h2>
      <form onSubmit={onLook} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="flex gap-2">
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="e.g. withdrawal rate, coast FIRE, expense ratio"
            className={`${inputCls} flex-1`}
          />
          <button type="submit" disabled={glossary.isPending} className={btnCls}>
            {glossary.isPending ? "…" : "Define"}
          </button>
        </div>
        {error && <div className="text-xs text-rose-400">{error}</div>}
        {result && (
          <p className="text-sm text-slate-300">
            <span className="font-medium">{result.term}:</span> {result.definition}
          </p>
        )}
      </form>
    </section>
  );
}

function ScenarioCard({
  data,
  onDelete,
}: {
  data: FireScenarioWithProjection;
  onDelete: () => void;
}) {
  const { scenario, projection } = data;
  const explain = useExplainFire();
  const [explanation, setExplanation] = useState<string | null>(null);

  const onExplain = async () => {
    try {
      const r = await explain.mutateAsync(scenario.id);
      setExplanation(r.explanation);
    } catch {
      setExplanation("Couldn't generate an explanation right now (AI budget or a transient error).");
    }
  };
  const indep =
    projection.projected_age_of_independence != null
      ? `age ${projection.projected_age_of_independence}`
      : projection.projected_year_of_independence != null
        ? `${projection.projected_year_of_independence}`
        : "not within horizon";
  const shortfall = projection.surplus_or_shortfall;

  const points = projection.years.map((y) => ({
    x: y.age ?? y.year,
    balance: Math.round(y.balance_cents / 100),
    target: Math.round(y.target_cents / 100),
  }));

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-medium">
            {scenario.name}{" "}
            <span className="text-xs uppercase tracking-wide text-slate-500">{scenario.variant}</span>
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Spend {formatCents(scenario.annual_spend_cents)}/yr · {Math.round(scenario.withdrawal_rate * 1000) / 10}% withdrawal ·{" "}
            {Math.round(scenario.real_return_rate * 1000) / 10}% real return ·{" "}
            contribute {formatCents(scenario.annual_contribution_cents)}/yr
            {scenario.part_time_income_cents ? ` · part-time ${formatCents(scenario.part_time_income_cents)}/yr` : ""}
          </div>
        </div>
        <button onClick={onDelete} className="text-xs text-slate-500 hover:text-rose-400">
          Delete
        </button>
      </div>

      <dl className="grid grid-cols-3 gap-2 text-sm">
        <Stat label="FIRE number" value={formatCents(projection.fire_number)} />
        <Stat label="Independence" value={indep} />
        <Stat
          label={shortfall >= 0 ? "Surplus" : "Shortfall"}
          value={formatCents(Math.abs(shortfall))}
          tone={shortfall >= 0 ? "good" : "bad"}
        />
      </dl>

      <div className="h-44">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
            <XAxis dataKey="x" stroke="#64748b" tick={{ fontSize: 11 }} />
            <YAxis
              stroke="#64748b"
              tick={{ fontSize: 11 }}
              width={64}
              tickFormatter={(v: number) => `$${Math.round(v / 1000)}k`}
            />
            <Tooltip
              contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 6, fontSize: 12 }}
              formatter={(v: number) => formatCents(v * 100)}
            />
            <ReferenceLine y={Math.round(projection.fire_number / 100)} stroke="#f59e0b" strokeDasharray="4 4" />
            <Line type="monotone" dataKey="balance" stroke="#60a5fa" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div>
        <button
          onClick={onExplain}
          disabled={explain.isPending}
          className="text-xs text-blue-400 hover:underline disabled:opacity-50"
        >
          {explain.isPending ? "Explaining…" : "Explain this projection"}
        </button>
        {explanation && (
          <p className="text-sm text-slate-300 mt-2 whitespace-pre-line border-l-2 border-slate-700 pl-3">
            {explanation}
          </p>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "good" | "bad" }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={tone === "good" ? "text-emerald-300" : tone === "bad" ? "text-rose-300" : ""}>{value}</dd>
    </div>
  );
}

function AddScenarioForm() {
  const create = useCreateFireScenario();
  const [name, setName] = useState("");
  const [variant, setVariant] = useState<string>("regular");
  const [spend, setSpend] = useState("50000");
  const [withdrawal, setWithdrawal] = useState("4");
  const [ret, setRet] = useState("6");
  const [contribution, setContribution] = useState("20000");
  const [partTime, setPartTime] = useState("0");
  const [stopAge, setStopAge] = useState("");
  const [targetAge, setTargetAge] = useState("");
  const [error, setError] = useState<string | null>(null);

  const applyPreset = (v: string) => {
    setVariant(v);
    const p = PRESETS[v];
    if (!p) return;
    if (p.annual_spend_cents != null) setSpend(String(p.annual_spend_cents / 100));
    if (p.withdrawal_rate != null) setWithdrawal(String(p.withdrawal_rate * 100));
    if (p.real_return_rate != null) setRet(String(p.real_return_rate * 100));
    setPartTime(String((p.part_time_income_cents ?? 0) / 100));
    setTargetAge(p.target_retirement_age != null ? String(p.target_retirement_age) : "");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Name the scenario.");
      return;
    }
    const input: CreateFireScenarioInput = {
      name: name.trim(),
      variant,
      annual_spend_cents: Math.round(parseFloat(spend || "0") * 100),
      withdrawal_rate: parseFloat(withdrawal || "4") / 100,
      real_return_rate: parseFloat(ret || "6") / 100,
      annual_contribution_cents: Math.round(parseFloat(contribution || "0") * 100),
      part_time_income_cents: Math.round(parseFloat(partTime || "0") * 100),
      stop_contributing_age: stopAge ? Number(stopAge) : null,
      target_retirement_age: targetAge ? Number(targetAge) : null,
    };
    try {
      await create.mutateAsync(input);
      setName("");
    } catch (err) {
      setError(String(err));
    }
  };

  return (
    <form onSubmit={onSubmit} className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="My plan" className={inputCls} />
      </Field>
      <Field label="Variant">
        <select value={variant} onChange={(e) => applyPreset(e.target.value)} className={inputCls}>
          {VARIANTS.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </Field>
      <Field label="Annual spend ($)">
        <input inputMode="decimal" value={spend} onChange={(e) => setSpend(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Annual contribution ($)">
        <input inputMode="decimal" value={contribution} onChange={(e) => setContribution(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Withdrawal rate (%)">
        <input inputMode="decimal" value={withdrawal} onChange={(e) => setWithdrawal(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Real return (%)">
        <input inputMode="decimal" value={ret} onChange={(e) => setRet(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Part-time income ($/yr)">
        <input inputMode="decimal" value={partTime} onChange={(e) => setPartTime(e.target.value)} className={inputCls} />
      </Field>
      <Field label="Stop contributing (age)">
        <input inputMode="numeric" value={stopAge} onChange={(e) => setStopAge(e.target.value)} placeholder="—" className={inputCls} />
      </Field>
      <Field label="Target retirement (age)">
        <input inputMode="numeric" value={targetAge} onChange={(e) => setTargetAge(e.target.value)} placeholder="—" className={inputCls} />
      </Field>
      <div className="col-span-2 sm:col-span-4 flex items-center justify-between gap-3">
        {error && <div className="text-xs text-rose-400">{error}</div>}
        <button type="submit" disabled={create.isPending} className={btnCls}>
          {create.isPending ? "Adding…" : "Add scenario"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  "rounded border border-slate-700 bg-slate-950/40 px-3 py-2 text-slate-100 placeholder-slate-600 focus:outline-none focus:border-slate-500";
const btnCls =
  "rounded bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-sm font-medium px-4 py-2 transition";
